/**
 * The names people see for jflo's pages, by path: "Imports › Price Lists" for
 * /imports/price-lists/<id>. The same names as the left menu (frontend/src/navigation/nav-items.ts)
 * and the report catalogue (frontend/src/reports/catalog.ts); frontend/src/navigation/page-names.test.ts
 * fails when they drift. The worker uses it to name links for AI prompts (the task summary, T-318).
 */

export interface PageName {
  path: string;
  name: string;
}

export const PAGE_NAMES: readonly PageName[] = [
  { path: '/', name: 'Home' },
  { path: '/home', name: 'Home' },
  { path: '/tasks', name: 'Tasks' },
  { path: '/inbox', name: 'Inbox' },
  { path: '/inbox-settings', name: 'Inbox › Inbox settings' },
  { path: '/policies', name: 'Policies' },
  { path: '/products', name: 'Products' },
  { path: '/product-details', name: 'Products › a product' },
  { path: '/product-batches', name: 'Products › Batch Upload' },
  { path: '/media', name: 'Products › Media' },
  { path: '/collections', name: 'Products › Collections' },
  { path: '/landing-pages', name: 'Products › Landing pages' },
  { path: '/vendors', name: 'Products › Brands' },
  { path: '/product-types', name: 'Products › Product Types' },
  { path: '/swatches', name: 'Products › Swatches' },
  { path: '/labels', name: 'Labels' },
  { path: '/suppliers', name: 'Suppliers' },
  { path: '/suppliers/catalogues', name: 'Suppliers › Supplier Catalogues' },
  { path: '/reports/supplier-changes', name: 'Suppliers › Supplier Changes' },
  { path: '/planning', name: 'Planning' },
  { path: '/planning/purchase-orders', name: 'Planning › Purchase Orders' },
  { path: '/reports/overstock', name: 'Planning › Overstock' },
  { path: '/reports/markdowns', name: 'Planning › Markdowns' },
  { path: '/imports', name: 'Imports' },
  { path: '/imports/suppliers', name: 'Imports › a supplier' },
  { path: '/imports/price-lists', name: 'Imports › Price Lists' },
  { path: '/imports/shipments', name: 'Imports › Shipments' },
  { path: '/imports/price-changes', name: 'Imports › Price Changes' },
  { path: '/imports/settings', name: 'Imports › Settings' },
  { path: '/receiving', name: 'Receiving' },
  { path: '/wholesale', name: 'Wholesale' },
  { path: '/wholesale/pricing', name: 'Wholesale › Pricing' },
  { path: '/wholesale/price-lists', name: 'Wholesale › Price Lists' },
  { path: '/wholesale/orders', name: 'Wholesale › Orders' },
  { path: '/wholesale/customers', name: 'Wholesale › Customers' },
  { path: '/wholesale/portal', name: 'Wholesale › Portal' },
  { path: '/trade', name: 'Trade' },
  { path: '/trade/orders', name: 'Trade › Orders' },
  { path: '/trade/customers', name: 'Trade › Customers' },
  { path: '/trade/discounts', name: 'Trade › Discounts' },
  { path: '/trade/portal', name: 'Trade › Portal' },
  { path: '/reports', name: 'Reports' },
  { path: '/reports/sales-dashboard', name: 'Reports › Sales Dashboard' },
  { path: '/reports/forecast', name: 'Reports › Forecast' },
  { path: '/reports/price-changes', name: 'Reports › Price Changes' },
  { path: '/reports/out-of-stock', name: 'Reports › Out of Stock' },
  { path: '/reports/sku-stock-performance', name: 'Reports › SKU Stock Performance' },
  { path: '/reports/unit-sales', name: 'Reports › Unit Sales' },
  { path: '/reports/shopify-sales', name: 'Reports › Shopify Sales' },
  { path: '/reports/shopify-payments', name: 'Reports › Shopify Payments' },
  { path: '/reports/time-spent', name: 'Reports › Time Spent' },
  { path: '/reports/order-reconciliation', name: 'Reports › Order Reconciliation' },
  { path: '/reports/xero-stock-not-on-shopify', name: 'Reports › Xero Stock Not on Shopify' },
  { path: '/reports/orphan-variant-revenue', name: 'Reports › Orphan Variants with Revenue' },
  { path: '/reports/collection-problems', name: 'Reports › Collection Problems' },
  { path: '/reports/unseen-stock', name: 'Reports › Unseen Stock' },
  { path: '/reports/swatch-problems', name: 'Reports › Swatch Problems' },
  { path: '/reports/meta-ads-check', name: 'Reports › Meta Ads Check' },
  { path: '/reports/google-ads-check', name: 'Reports › Ads Check' },
  { path: '/reports/wasted-ad-spend', name: 'Reports › Wasted Ad Spend' },
  { path: '/reports/capacity-planner', name: 'Reports › Capacity Planner' },
  { path: '/reports/inbox-workload', name: 'Reports › Inbox Workload' },
  { path: '/marketing', name: 'Marketing' },
  { path: '/marketing/instagram', name: 'Marketing › Instagram' },
  { path: '/marketing/social-picks', name: 'Marketing › Social picks' },
  { path: '/marketing/post-ideas', name: 'Marketing › Post ideas' },
  { path: '/marketing/facebook', name: 'Marketing › Facebook' },
  { path: '/marketing/meta-ads', name: 'Marketing › Meta Ads' },
  { path: '/marketing/google-ads', name: 'Marketing › Google Ads' },
  { path: '/marketing/microsoft-ads', name: 'Marketing › Microsoft Ads' },
  { path: '/marketing/seo', name: 'Marketing › SEO' },
  { path: '/marketing/newsletters', name: 'Marketing › Newsletters' },
  { path: '/marketing/notifications', name: 'Marketing › Customer notifications' },
  { path: '/marketing/settings', name: 'Marketing › Settings' },
  { path: '/ai-usage', name: 'AI › Usage' },
  { path: '/ai-prompts', name: 'AI › Prompts' },
  { path: '/settings', name: 'Settings' },
];

const UUID_IN_PATH = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/|$)/i;

function pathOf(url: string): string | null {
  try {
    return new URL(url, 'https://jflo.app').pathname.replace(/\/+$/, '') || '/';
  } catch {
    return null;
  }
}

/**
 * The page a jflo path or URL is on, by the longest matching path: "Imports › Price Lists" for
 * /imports/price-lists, "Imports › Price Lists (one record)" for /imports/price-lists/<id>?tab=costing.
 * Null for a path jflo doesn't name.
 */
export function pageNameFor(url: string): string | null {
  const path = pathOf(url);
  if (!path) return null;
  let best: PageName | null = null;
  for (const page of PAGE_NAMES) {
    const matches = page.path === '/' ? path === '/' : path === page.path || path.startsWith(`${page.path}/`);
    if (matches && (!best || page.path.length > best.path.length)) best = page;
  }
  if (!best) return null;
  // "a product", "a supplier": the name already says it's one record.
  const oneRecord = path !== best.path && UUID_IN_PATH.test(path) && !/ a [a-z]+$/.test(best.name);
  return oneRecord ? `${best.name} (one record)` : best.name;
}
